'use client'

import { getOneTrustAllowedCategoriesClient } from '@patrianna/analytics'
import { setCookie, getCookie } from '@patrianna/shared-utils'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { getSafeLocalStorageAccessor } from 'src/utils/localStorage'

interface ContextState {
  isDarkMode?: boolean
  changeThemeMode?: () => void
}

const ThemeContext = createContext({} as ContextState)

const isFunctionalCookiesAllowed = () => getOneTrustAllowedCategoriesClient()?.includes('C0003') ?? false

type Props = {
  children: ReactNode
  initialDarkMode?: boolean
}

function ThemeProvider(props: Props) {
  const getInitialTheme = () => {
    if (props.initialDarkMode !== undefined) {
      return props.initialDarkMode
    }

    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') === 'dark'
    }

    return false
  }

  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme)

  const applyThemeMode = (darkMode: boolean) => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'default')
    setIsDarkMode(darkMode)
  }

  const persistThemeMode = (darkMode: boolean) => {
    if (isFunctionalCookiesAllowed()) {
      try {
        const storage = getSafeLocalStorageAccessor()
        storage?.setItem('DARK_MODE', `${darkMode}`)
      } catch (err) {
        console.log(err)
      }

      setCookie('DARK_MODE', `${darkMode}`)
    }
  }

  const setThemeAndPreserve = (darkMode: boolean) => {
    applyThemeMode(darkMode)
    persistThemeMode(darkMode)
  }

  const changeThemeMode = () => {
    setThemeAndPreserve(!isDarkMode)
    if (isFunctionalCookiesAllowed()) {
      setCookie('THEME_ORIGIN', 'user')
    }
  }

  // eslint-disable-next-line
  const value = useMemo(() => ({ isDarkMode, changeThemeMode }), [isDarkMode])

  useEffect(() => {
    try {
      // it makes sense to relay on the localStorage value at first
      // because user could use several devices with different theme
      const storage = getSafeLocalStorageAccessor()
      const isDarkModeStorage: boolean | null = JSON.parse(storage?.getItem('DARK_MODE') ?? 'null')
      const isDarkModeCookie: boolean | null = JSON.parse(getCookie('DARK_MODE', document.cookie) ?? 'null')
      const isDarkModeStored = isDarkModeStorage !== null ? isDarkModeStorage : isDarkModeCookie
      const userDefinedTheme = getCookie('THEME_ORIGIN', document.cookie) === 'user'
      const darkModeMedia = window.matchMedia('(prefers-color-scheme: dark)')

      if (isDarkModeStored !== null) {
        applyThemeMode(isDarkModeStored)

        if (userDefinedTheme) {
          persistThemeMode(isDarkModeStored)
        }

        return
      }

      // if user haven't picked the theme it'll be set in accordance to the system preferences
      // but will be not preserved in the localStorage and cookies
      const onThemeMediaChange = () => {
        applyThemeMode(darkModeMedia.matches)
      }

      applyThemeMode(darkModeMedia.matches)
      darkModeMedia.addEventListener('change', onThemeMediaChange)

      return () => darkModeMedia.removeEventListener('change', onThemeMediaChange)
    } catch (err) {
      console.log(err)
    }
  }, [])

  return <ThemeContext.Provider value={value}>{props.children}</ThemeContext.Provider>
}

const useThemeContext = () => useContext(ThemeContext)

export { useThemeContext, ThemeProvider }
